import React, { useState } from 'react';

import { Markdown } from '@/components/Markdown';
import { CopyButton } from '@/components/ui/copy-button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

interface PromptSheetProps {
  turnId: string | null;
  prompt: string | null;
  onClose: () => void;
}

/** The system prompt the agent sent for one turn, rendered or raw. */
export const PromptSheet: React.FC<PromptSheetProps> = ({ turnId, prompt, onClose }) => {
  const [view, setView] = useState<'rendered' | 'raw'>('rendered');

  return (
    <Sheet open={turnId !== null} onOpenChange={open => !open && onClose()}>
      <SheetContent side="right" className="flex w-full flex-col sm:max-w-2xl">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            System prompt
            {prompt && <CopyButton value={prompt} label="Prompt" />}
          </SheetTitle>
          <SheetDescription className="font-mono text-xs">Turn {turnId}</SheetDescription>
        </SheetHeader>
        {prompt === null ? (
          <p className="text-sm text-muted-foreground">No prompt was recorded for this turn.</p>
        ) : (
          <Tabs
            value={view}
            onValueChange={v => setView(v as 'rendered' | 'raw')}
            className="flex min-h-0 flex-1 flex-col"
          >
            <TabsList className="self-start">
              <TabsTrigger value="rendered">Rendered</TabsTrigger>
              <TabsTrigger value="raw">Raw</TabsTrigger>
            </TabsList>
            <TabsContent value="rendered" className="min-h-0 flex-1 overflow-auto">
              <Markdown className="text-sm">{prompt}</Markdown>
            </TabsContent>
            <TabsContent value="raw" className="min-h-0 flex-1 overflow-auto">
              <pre className="whitespace-pre-wrap break-words rounded bg-muted p-2 text-xs">
                {prompt}
              </pre>
            </TabsContent>
          </Tabs>
        )}
      </SheetContent>
    </Sheet>
  );
};
